import React, { useEffect, useState } from 'react'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { I18n } from 'react-redux-i18n'

import { updateActionsErrors } from 'redux/smartchat/details/actions'
import { setAlternativesActionsErrors } from 'redux/smartchat/alternatives/actions'
import VirtualAssistantViewUI from './VirtualAssistantViewUI'
import { getDataOptions } from 'components/SmartChatView/utils'

const VirtualAssistantView = ({ renderType, label, placeholder, dataWay, currentValue, onChange, tooltip, error }) => {
  const { modalActionRef } = useSelector(store => ({
    modalActionRef: dataWay?.type === 'alternatives'
      ? store.smartchatAlternatives.alternativesModalActionRef.find(modalActionRef => modalActionRef.alternativesId === dataWay.alternativesId) || {}
      : store.smartchatDetails.modalActionRef
  }), shallowEqual)
  const dispatch = useDispatch()
  const [value, setValue] = useState({})
  const [dataToExport, setDataToExport] = useState(currentValue || {})
  const [renderedFields, setRenderedFields] = useState([])
  const [vaOptions, setVaOptions] = useState(null)
  const [vaCategoriesOptions, setVaCategoriesOptions] = useState(null)
  const [vaFaqsOptions, setVaFaqsOptions] = useState(null)
  const [loadingVaOptions, setLoadingVaOptions] = useState(false)
  const [loadingVaCategoriesOptions, setLoadingVaCategoriesOptions] = useState(false)
  const [loadingVaFaqsOptions, setLoadingVaFaqsOptions] = useState(false)
  const [previewIntegrity, setPreviewIntegrity] = useState({})

  useEffect(() => {
    let isSubscribed = true

    if (isSubscribed) {
      if (!vaOptions) getVaOptions()
      if (currentValue.consoleId || currentValue.categoryName) getVaCategoriesOptions(currentValue.consoleId)
      if (currentValue.categoryName || currentValue.intentId) getVaFaqsOptions(currentValue.consoleId, currentValue.categoryName)
    }

    return () => isSubscribed = false
  }, [currentValue.consoleId, currentValue.categoryName])

  // Current data
  useEffect(() => {
    let isSubscribed = true

    if (isSubscribed) {
      if (!currentValue || !Object.keys(currentValue).length) return

      if (renderType === 'action') {
        if (modalActionRef.mode !== 'update') return
        onChange(currentValue)
      }

      const error = { FAQ: {} }
      const updatedValue = { ...value }
      const updatedPreviewIntegrity = { ...previewIntegrity }
      const updatedRenderedFields = [...renderedFields]

      if (vaOptions?.length) {
        const option = vaOptions.find(va => va.value === currentValue.consoleId)
        if (option) {
          if (updatedPreviewIntegrity.virtualAssistant) updatedPreviewIntegrity.virtualAssistant = null
          updatedValue.virtualAssistant = option
          updatedRenderedFields.push('virtualAssistant')
        } else {
          // Error
          if (currentValue.consoleId) {
            if (renderType === 'action') {
              if (updatedValue.virtualAssistant !== null) error.FAQ.virtualAssistant = I18n.t('smartchat_tooltip_warning_notFound')
              updatedRenderedFields.push('virtualAssistant')
            } else {
              updatedPreviewIntegrity.virtualAssistant = I18n.t('smartchat_tooltip_warning_notFound')
            }
          } else {
            updatedValue.virtualAssistant = null
          }
        }
      }

      if (vaCategoriesOptions?.length) {
        const option = vaCategoriesOptions.find(vaCategory => vaCategory.value === currentValue.categoryId)
        if (option) {
          if (updatedPreviewIntegrity.virtualAssistantCategory) updatedPreviewIntegrity.virtualAssistantCategory = null
          updatedValue.virtualAssistantCategory = option
        } else {
          // Error
          if (currentValue.consoleId && !currentValue.categoryId) {
            if (renderType === 'action') {
              if (updatedValue.virtualAssistantCategory !== null) error.FAQ.virtualAssistantCategory = I18n.t('smartchat_tooltip_warning_category')
            } else {
              updatedPreviewIntegrity.virtualAssistantCategory = I18n.t('smartchat_tooltip_warning_category')
            }
          }
        }
      }

      if (vaFaqsOptions?.length) {
        const option = vaFaqsOptions.find(vaFaq => vaFaq.value === currentValue.intentId)
        if (option) {
          if (updatedPreviewIntegrity.virtualAssistantFaq) updatedPreviewIntegrity.virtualAssistantFaq = null
          updatedValue.virtualAssistantFaq = option
          updatedRenderedFields.push('virtualAssistantFaq')
        } else {
          // Error
          if (currentValue.intentId) {
            if (renderType === 'action') {
              if (updatedValue.virtualAssistantFaq !== null) error.FAQ.virtualAssistantFaq = I18n.t('smartchat_tooltip_warning_notFound')
              updatedRenderedFields.push('virtualAssistantFaq')
            } else {
              updatedPreviewIntegrity.virtualAssistantFaq = I18n.t('smartchat_tooltip_warning_notFound')
            }
          } else {
            updatedValue.virtualAssistantFaq = null
          }
        }
      }

      // Update local data
      setValue(updatedValue)

      // Render fields
      if (renderType === 'action') setRenderedFields(updatedRenderedFields)

      // Error reports
      if (renderType === 'action') {
        if (Object.keys(error.FAQ).length) dataWay?.type === 'alternatives'
          ? dispatch(setAlternativesActionsErrors({ alternativesId: dataWay.alternativesId, actionsErrors: error }))
          : dispatch(updateActionsErrors(error))
      } else {
        if (Object.keys(updatedPreviewIntegrity).length) setPreviewIntegrity(updatedPreviewIntegrity)
      }
    }

    return () => isSubscribed = false
  }, [currentValue, vaOptions, vaCategoriesOptions, vaFaqsOptions])

  const getVaOptions = async () => {
    setLoadingVaOptions(true)
    try {
      const { vaOptions } = await getDataOptions({ requestedData:  ['vaOptions'] })
      setVaOptions(vaOptions)
    } finally {
      setLoadingVaOptions(false)
    }
  }

  const getVaCategoriesOptions = async (consoleId) => {
    setLoadingVaCategoriesOptions(true)
    try {
      const { vaCategoriesOptions } = await getDataOptions({ requestedData:  ['vaCategoriesOptions'], params: { consoleId } })
      setVaCategoriesOptions(vaCategoriesOptions)
    } finally {
      setLoadingVaCategoriesOptions(false)
    }
  }

  const getVaFaqsOptions = async (consoleId, categoryName) => {
    setLoadingVaFaqsOptions(true)
    try {
      const { vaFaqsOptions } = await getDataOptions({ requestedData:  ['vaFaqsOptions'], params: { consoleId, categoryName } })
      setVaFaqsOptions(vaFaqsOptions)
    } finally {
      setLoadingVaFaqsOptions(false)
    }
  }

  const onChangeInner = ({ fieldType, fieldData }) => {
    let updatedRenderedFields = [...renderedFields]
    const updatedValue = { ...value }
    const updatedDataToExport = { ...dataToExport }

    // Data to export
    if (fieldType === 'virtualAssistant') {
      if (fieldData) {
        updatedValue.virtualAssistant = fieldData
        updatedDataToExport.consoleId = fieldData.value
      } else {
        updatedValue.virtualAssistant = null
        updatedDataToExport.consoleId = null
      }
      // Clear later fields and data
      if (updatedValue.virtualAssistantCategory) {
        setVaCategoriesOptions([])
        updatedValue.virtualAssistantCategory = null
        updatedDataToExport.categoryId = null
        updatedDataToExport.categoryName = null
      }
      if (updatedValue.virtualAssistantFaq) {
        if (renderType === 'action') updatedRenderedFields = updatedRenderedFields.filter(renderedField => renderedField !== 'virtualAssistantFaq')
        setVaFaqsOptions([])
        updatedValue.virtualAssistantFaq = null
        updatedDataToExport.intentId = null
      }
    }
    if (fieldType === 'virtualAssistantCategory') {
      if (fieldData) {
        updatedValue.virtualAssistantCategory = fieldData
        updatedDataToExport.categoryId = fieldData.value
        updatedDataToExport.categoryName = fieldData.label
      } else {
        updatedDataToExport.categoryId = null
        updatedDataToExport.categoryName = null
      }
      // Clear later fields and data
      if (updatedValue.virtualAssistantFaq) {
        if (renderType === 'action') updatedRenderedFields = updatedRenderedFields.filter(renderedField => renderedField !== 'virtualAssistantFaq')
        setVaFaqsOptions([])
        updatedValue.virtualAssistantFaq = null
        updatedDataToExport.intentId = null
      }
    }
    if (fieldType === 'virtualAssistantFaq') {
      if (fieldData) {
        updatedValue.virtualAssistantFaq = fieldData
        updatedDataToExport.intentId = fieldData.value
      } else {
        updatedValue.virtualAssistantFaq = null
        updatedDataToExport.intentId = null
      }
    }

    // Update local data
    setValue(updatedValue)
    setDataToExport(updatedDataToExport)

    // Update rendered fields
    setRenderedFields(updatedRenderedFields)

    // Export the data
    onChange(updatedDataToExport)
  }

  return (
    <VirtualAssistantViewUI
      renderType={renderType}
      label={label}
      placeholder={placeholder}
      renderedFields={renderedFields}
      vaOptions={vaOptions}
      vaCategoriesOptions={vaCategoriesOptions}
      vaFaqsOptions={vaFaqsOptions}
      value={value}
      tooltip={tooltip}
      setRenderedFields={setRenderedFields}
      onChangeInner={onChangeInner}
      getVaOptions={getVaOptions}
      getVaCategoriesOptions={getVaCategoriesOptions}
      getVaFaqsOptions={getVaFaqsOptions}
      loadingVaOptions={loadingVaOptions}
      loadingVaCategoriesOptions={loadingVaCategoriesOptions}
      loadingVaFaqsOptions={loadingVaFaqsOptions}
      error={error}
      previewIntegrity={previewIntegrity}
    />
  )
}

export default VirtualAssistantView
